import React from 'react';
import { Link } from 'react-router-dom';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { TEACHERS_STUDENT_PORTFOLIO_ASSIGNMENT_DETAILS_URL } from 'config/urls';
import { AssignmentStatusOptions } from 'constants/assignments';
import { ReactComponent as CopyIcon } from 'images/copy.svg';
import { get, range } from 'lodash';
import { copyToClipboard } from 'utils';

import { colors } from 'theme/palette';
import { reverse } from 'utils/urls';

import AssignmentStatusIcon from 'components/AssignmentStatusIcon';
import Button from 'components/Button';
import Typography from 'components/Typography';

import { LoadingListViewRowSkeleton } from '../LoadingListViewSkeleton';
import styles from './styles.module.scss';
import { formatDate } from './utils';

const HeaderCell = ({ children }) => (
  <TableCell className={styles.headerCell}>
    <Typography variant="H-TEXT-3" color={colors.blue1}>
      {children}
    </Typography>
  </TableCell>
);

const Cell = ({ children }) => (
  <TableCell className={styles.cell}>{children}</TableCell>
);

const StatusIcon = ({ status }) => {
  const { icon: Icon, color } = get(AssignmentStatusIcon, status);
  return <Icon fill={color} />;
};

const AssignmentRow = ({ assignment, studentId }) => {
  const tasksCount = assignment.tasks.length;
  const linkToDetails = reverse(
    TEACHERS_STUDENT_PORTFOLIO_ASSIGNMENT_DETAILS_URL,
    {
      studentId,
      assignmentId: assignment.id
    }
  );

  const assignedTasksCount = assignment.tasks.filter(
    (task) => task.status === AssignmentStatusOptions.ASSIGNED
  ).length;

  return (
    <TableRow hover>
      <Cell>
        <Link to={linkToDetails} className={styles.noUnderline}>
          <Typography variant="B-Text-2" color={colors.grey1}>
            {assignment.name}
          </Typography>
        </Link>
      </Cell>
      <Cell>
        <Typography variant="B-Text-2" color={colors.grey2}>
          {formatDate(assignment.due_at)}
        </Typography>
      </Cell>
      <Cell>
        <Typography variant="B-Text-2" color={colors.grey1}>
          <StatusIcon status={assignment.overall_status} />
        </Typography>
      </Cell>
      <Cell>
        <Typography variant="B-Text-2" color={colors.grey1}>
          {tasksCount - assignedTasksCount} / {tasksCount}
        </Typography>
      </Cell>
      <Cell>
        <Typography variant="B-Text-2" color={colors.grey1}>
          {assignment.complete_tasks_count} / {tasksCount}
        </Typography>
      </Cell>
      <Cell>
        {assignment.tasks.map((task, index) => (
          <>
            {task.work.length > 0 && <span key={index}>{index + 1} </span>}
            {task.work.length === 0 && <>{index + 1} </>}
          </>
        ))}
      </Cell>
      <Cell>
        <Button
          noTypography
          variant="textAndIcon"
          color="transparent"
          onClick={() => copyToClipboard(assignment.student_deeplink)}
        >
          <CopyIcon />
          <Typography
            display="inline"
            variant="S-TEXT-1"
            color={colors.blue4}
            className={styles.copyLinkText}
          >
            Copy Link
          </Typography>
        </Button>
      </Cell>
    </TableRow>
  );
};

// `loading` is used for the "Load more" feature
const ListView = ({ assignments, studentId, loading }) => (
  <TableContainer variant="outlined" classes={{ root: styles.tableContainer }}>
    <Table className={styles.table}>
      <TableHead>
        <TableRow>
          <HeaderCell>Name</HeaderCell>
          <HeaderCell>Due</HeaderCell>
          <HeaderCell>Overall Status</HeaderCell>
          <HeaderCell>Tasks Submitted</HeaderCell>
          <HeaderCell>Tasks Complete</HeaderCell>
          <HeaderCell>Tasks </HeaderCell>
          <HeaderCell>Student Link</HeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {assignments.map((assignment) => (
          <AssignmentRow
            key={assignment.id}
            assignment={assignment}
            studentId={studentId}
          />
        ))}
        {loading && range(3).map((x) => <LoadingListViewRowSkeleton key={x} />)}
      </TableBody>
    </Table>
  </TableContainer>
);

export default ListView;
